import { useEffect, useState } from 'react';
import { leadsService } from '../lib/services';
import { useConsultantOptions } from '../lib/useDirectory';
import { useToast } from '../lib/toast';

/**
 * Assign / unassign / change the sales consultant on a lead, plus a one-click
 * rules-based auto-assign. Self-contained: fetches the consultant directory,
 * calls the lead endpoints, and hands the updated lead back to the parent.
 *
 * Props:
 *   leadId               — required
 *   assignedConsultantId — current assignment (nullable)
 *   onChanged(lead)      — called with the updated lead after any action
 *   compact              — tighter layout for inline/row use
 */
export default function AssignConsultant({ leadId, assignedConsultantId, onChanged, compact }) {
  const { options: consultants } = useConsultantOptions();
  const { toast } = useToast();
  const [selected, setSelected] = useState(assignedConsultantId || '');
  const [busy, setBusy] = useState(false);

  useEffect(() => { setSelected(assignedConsultantId || ''); }, [assignedConsultantId]);

  const run = async (fn, okMsg) => {
    setBusy(true);
    try {
      const lead = await fn();
      onChanged?.(lead);
      toast(okMsg, { variant: 'success' });
    } catch (e) {
      toast(e?.response?.data?.message || e?.message || 'Action failed', { variant: 'error' });
    } finally {
      setBusy(false);
    }
  };

  const dirty = (selected || '') !== (assignedConsultantId || '');
  const nameOf = (id) => consultants.find((c) => c.id === id)?.primary || (id ? `Consultant ${String(id).slice(0, 8)}` : 'Unassigned');

  return (
    <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
      <select
        className="btn small"
        value={selected}
        disabled={busy}
        onChange={(e) => setSelected(e.target.value)}
        style={{ minWidth: compact ? 150 : 200 }}
        title={`Currently: ${nameOf(assignedConsultantId)}`}
      >
        <option value="">Unassigned</option>
        {consultants.map((c) => <option key={c.id} value={c.id}>{c.primary}</option>)}
      </select>

      {dirty && selected && (
        <button type="button" className="btn small primary" disabled={busy}
          onClick={() => run(() => leadsService.reassign(leadId, selected),
            assignedConsultantId ? 'Consultant changed' : 'Consultant assigned')}>
          {assignedConsultantId ? 'Change' : 'Assign'}
        </button>
      )}

      {assignedConsultantId && (
        <button type="button" className="btn small" disabled={busy}
          onClick={() => run(() => leadsService.unassign(leadId), 'Consultant removed')}>
          Unassign
        </button>
      )}

      <button type="button" className="btn small" disabled={busy}
        onClick={() => run(() => leadsService.autoAssign(leadId), 'Auto-assigned by rules')}
        title="Assign using the dealer's assignment rules">
        ⚡ Auto
      </button>
    </div>
  );
}
